import { EnhancedStore } from '@reduxjs/toolkit';
import { logger } from '../utils/logger';
import {
  activated,
  attached,
  created,
  detached,
  moved,
  removed,
  replaced,
  updated,
  windowCreated,
  windowFocused,
  windowRemoved,
  zoomChanged,
} from '../store/windowsSlice';

export interface TabsListenersOptions {
  shouldIgnoreUrl?: (url: string) => boolean;
}

export function registerTabsListeners(store: EnhancedStore, options?: TabsListenersOptions) {
  const browser = chrome;
  const shouldIgnore = options?.shouldIgnoreUrl ?? (() => false);

  const checkIgnore = async (tabId: number, knownUrl?: string) => {
    if (knownUrl !== undefined && knownUrl !== '') {
      return shouldIgnore(knownUrl);
    }
    try {
      const tab = await browser.tabs.get(tabId);
      if (tab.url) {
        return shouldIgnore(tab.url);
      }
    } catch (e) {
      // Ignore errors (e.g. tab closed)
    }
    return false;
  };

  browser.tabs.onActivated.addListener(async (activeInfo) => {
    try {
      const chromeTab = await browser.tabs.get(activeInfo.tabId);
      if (chromeTab.url && shouldIgnore(chromeTab.url)) return;
      store.dispatch(
        activated({ windowId: activeInfo.windowId, tabId: activeInfo.tabId, chromeTab: chromeTab })
      );
      await chrome.storage.session.set({ [`wt_${activeInfo.windowId}`]: Date.now() }).catch((err: unknown) => logger.error('[TabsListeners] session.set failed (onActivated)', err));
    } catch (e) {
      console.debug('Could not get activated tab (may be closed):', e);
    }
  });

  browser.tabs.onAttached.addListener(async (tabId, attachInfo) => {
    if (await checkIgnore(tabId)) return;
    store.dispatch(
      attached({
        tabId,
        ...attachInfo,
      })
    );
  });

  browser.tabs.onCreated.addListener(async (chromeTab) => {
    if (chromeTab.id && await checkIgnore(chromeTab.id, chromeTab.url)) return;
    store.dispatch(created({ chromeTab }));
  });

  browser.tabs.onDetached.addListener(async (tabId, detachInfo) => {
    if (await checkIgnore(tabId)) return;
    store.dispatch(
      detached({
        tabId,
        ...detachInfo,
      })
    );
  });

  browser.tabs.onMoved.addListener(async (tabId, moveInfo) => {
    if (await checkIgnore(tabId)) return;
    store.dispatch(
      moved({
        tabId,
        ...moveInfo,
      })
    );
    await chrome.storage.session.set({ [`wt_${moveInfo.windowId}`]: Date.now() }).catch((err: unknown) => logger.error('[TabsListeners] session.set failed (onMoved)', err));
  });

  browser.tabs.onRemoved.addListener(async (tabId, removeInfo) => {
    store.dispatch(
      removed({
        tabId,
        ...removeInfo,
      })
    );
  });

  browser.tabs.onReplaced.addListener(async (addedTabId, removedTabId) => {
    if (await checkIgnore(addedTabId)) return;
    store.dispatch(replaced({ addedTabId, removedTabId }));
  });

  browser.tabs.onUpdated.addListener(async (tabId, changeInfo, chromeTab) => {
    if (await checkIgnore(tabId, chromeTab.url)) return;
    store.dispatch(updated({ tabId, changeInfo, chromeTab }));
  });

  browser.tabs.onZoomChange.addListener(async (zoomChangeInfo) => {
    if (await checkIgnore(zoomChangeInfo.tabId)) return;
    store.dispatch(zoomChanged(zoomChangeInfo));
  });

  browser.windows.onBoundsChanged.addListener(async (window) => {});

  browser.windows.onCreated.addListener(async (window) => {
    store.dispatch(windowCreated({ window }));
  });

  browser.windows.onFocusChanged.addListener(async (windowId) => {
    if (windowId === browser.windows.WINDOW_ID_NONE) {
      return;
    }
    store.dispatch(windowFocused({ windowId }));
    await chrome.storage.session.set({ [`wt_${windowId}`]: Date.now() }).catch((err: unknown) => logger.error('[TabsListeners] session.set failed (onFocusChanged)', err));
    try {
      const activeInfo = await browser.tabs.query({ active: true, windowId: windowId });
      const chromeTab = activeInfo[0];
      if (chromeTab && chromeTab.id) {
        if (chromeTab.url && shouldIgnore(chromeTab.url)) return;
        store.dispatch(
          activated({ windowId: windowId, tabId: chromeTab.id, chromeTab: chromeTab })
        );
      }
    } catch (e) {
      console.debug('Could not get active tab in focused window:', e);
    }
  });

  browser.windows.onRemoved.addListener(async (windowId) => {
    store.dispatch(windowRemoved({ windowId }));
  });
}
